/**
 * 账户工厂
 * @author jianbo
 * @time 2020-03-13
 *
 * AccountFactory.getCurrentUser() 获取当前登录用户信息
*/
import store from '../store'
import AccountService from './AccountService'
import AccountModel from './AccountModel'
import TokenStorage from './TokenStorage'
import { IS_IOS_APP, IS_ANDROID_APP, IS_APP_WEBVIEW } from '../utils/device'

const GET_TOKEN_CALLBACK = 'loginSuccess'
// 注册回调
window[GET_TOKEN_CALLBACK] = (res) => {
  const token = res
  store.dispatch('setToken', { token })
}

class AccountFactory {
  /**
   * 登录状态监测
  */
  static isLoggedin () {
    return store.getters.isLoggedin
  }

  // 尝试登录
  // APP内置浏览器尝试使用内置APP登录
  // 非APP内置浏览器尝试SSO登录
  static tryLogin (options) {
    if (IS_APP_WEBVIEW) {
      AccountFactory.tryLoginWithAPP(options)
    } else {
      AccountFactory.tryLoginWithSSO(options)
    }
  }

  /**
   * 尝试SSO登录
   */
  static tryLoginWithSSO (options) {
    // 当前连接
    const from = encodeURIComponent(window.location.href)
    // SSO 客户端
    const url = `/sso_client/auth?from=${from}`
    // 跳转
    if (options) {
      const { replace } = options
      if (replace) {
        window.location.replace(url)
        return
      }
    }
    window.location.href = url
  }

  // 尝试APP内嵌登录
  static tryLoginWithAPP () {
    try {
      AccountFactory.initToken()
    } catch (e) {}
    try {
      const route = {
        path: 'loginView',
        query: null
      }
      const jsonParam = JSON.stringify(route)
      // 打开登录视图
      if (IS_IOS_APP) {
        return window.webkit.messageHandlers.navigateTo.postMessage(jsonParam)
      }
      return window.wfc.navigateTo(jsonParam)
    } catch (e) {}
  }

  // 退出登录
  static logout () {
    AccountFactory.clearToken()
  }

  // 清空token
  static clearToken () {
    try {
      store.dispatch('clearToken')
      TokenStorage.removeToken()
    } catch (e) {
      return e
    }
  }

  // 初始化 token
  static initToken () {
    // 初始化 token
    // 1. 从本地存储中获取token
    // 2. tokne 放到 store 中
    const token = AccountFactory.getToken()
    if (token) {
      store.dispatch('setToken', { token })
    }

    // 验证及初始化当前登录用户信息
    // 1. 从服务端拉取用户信息,
    // 2. 存储到 store
    try {
      AccountFactory.fetchCurrentUser()
    } catch (e) {
      // @todo clear currentUserInfo and logout
    }
  }

  // 从内嵌APP或本地存储中获取Token
  static getToken () {
    // 这里必须return 不然会被覆盖
    if (IS_APP_WEBVIEW) {
      return AccountFactory.getTokenFromApp()
    }
    return TokenStorage.getToken()
  }

  // APP 内嵌浏览器中获取token
  static getTokenFromApp () {
    // 参数传递
    const param = {
      success: GET_TOKEN_CALLBACK,
      fail: null,
      params: null
    }
    // APP端获取token
    try {
      if (IS_IOS_APP) {
        // 注意, 这里传对象
        return window.webkit.messageHandlers.getUserToken.postMessage(param)
      }
      if (IS_ANDROID_APP) {
        // 注意, 安卓传JSON字符串
        return window.wfc.getUserToken(JSON.stringify(param))
      }
    } catch (e) {}
  }

  /**
   * 获取当前登录用信息
   * @return Object
   */
  static getCurrentUser () {
    return store.getters.userInfo
  }

  /**
   * 服务端拉取当前登录用户信息
   * @return Promise
   */
  static fetchCurrentUser () {
    return AccountService.getCurrentUser().then(res => {
      const account = res.data && res.data.code === 200 ? new AccountModel(res.data.data) : null
      store.dispatch('setUserInfo', account)
      return Promise.resolve(account)
    })
  }
}

export default AccountFactory
